import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

interface AnimatedEnvironmentPanelProps {
    tab: string;
    id: string;
    labelledBy: string;
    children: React.ReactNode;
}

export default function AnimatedEnvironmentPanel({ tab, id, labelledBy, children }: AnimatedEnvironmentPanelProps) {
    const shellRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const heightAnimation = useRef<Animation>();
    const fadeAnimation = useRef<Animation>();
    const previousTab = useRef(tab);
    const measuredHeight = useRef<number>();
    const [height, setHeight] = useState<number>();

    useLayoutEffect(() => {
        const shell = shellRef.current;
        const content = contentRef.current;
        if (!shell || !content) {
            return;
        }
        // Explicitly requested panel motion uses Web Animations, independent of global CSS motion resets.
        const measure = () => {
            const nextHeight = content.offsetHeight;
            if (nextHeight === measuredHeight.current) {
                return;
            }
            // Sample the currently animated size so rapid tab changes continue smoothly.
            const fromHeight = shell.offsetHeight;
            heightAnimation.current?.cancel();
            if (measuredHeight.current !== undefined && shell.animate) {
                heightAnimation.current = shell.animate(
                    [{ height: fromHeight + 'px' }, { height: nextHeight + 'px' }],
                    { duration: 360, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
                );
            }
            measuredHeight.current = nextHeight;
            setHeight(nextHeight);
        };
        measure();
        if (previousTab.current !== tab && content.animate) {
            fadeAnimation.current = content.animate(
                [{ opacity: 0 }, { opacity: 1 }],
                { duration: 280, delay: 60, fill: 'backwards', easing: 'ease-out' },
            );
        }
        previousTab.current = tab;
        // Observe content rather than the animated shell, including asynchronous embed data.
        const observer = new ResizeObserver(measure);
        observer.observe(content);
        return () => {
            observer.disconnect();
            heightAnimation.current?.cancel();
            fadeAnimation.current?.cancel();
        };
    }, [tab]);

    return (
        <Box ref={shellRef} role="tabpanel" id={id} aria-labelledby={labelledBy}
            sx={{ height: height === undefined ? 'auto' : height + 'px', overflow: 'hidden' }}>
            <Box key={tab} ref={contentRef} sx={{ display: 'flow-root' }}>
                {children}
            </Box>
        </Box>
    );
}
